import { IMapLegend } from "../api/map";
import { IGroupByMap } from "./groupBy";

// ThreeDViewer
export type KeyValue = Record<string, unknown>;

export type TxnT = {
  id: string;
  facilityId: string;
  legend: IMapLegend;
  facilityLegend: IMapLegend;
  bBox: {
    sw: [];
    ne: [];
  };
  ///the filter id that was used to generate the map
  filterId?: string;
};

export type ThreeDViewerOptionT = {
  scaleDrainage: boolean;
  scaleZ: number;
  onOptionChange: (option: KeyValue, refreshData: boolean) => void;
  showDrainage: boolean;
  showIPDBDrainage: boolean;
  showVerticalPortion: boolean;
  lineWidth: number;
  showIPDBPlane: boolean;
  showIPDBMesh: boolean;
  highlightHzLeg: boolean;
  showMultiLegWells: boolean;
  hzLegColor: number;
  wireframe: boolean;
  showWellLabel: boolean;
  showWellDetail: boolean;
  colorMap: string;
  ipdbField: string;
  ipdbFieldUnit: string;
  colorMapMin?: number;
  colorMapMax?: number;
  ipdbSource: string;
  ipdbSources: string[];
  rightLateralDistance: number;
  upVerticalDistance: number;
  leftLateralDistance: number;
  downVerticalDistance: number;
  maxValue: number;
  showVertical: boolean;
  modelSource: string;
  modelSources: { label: string; options: { label: string; value: string }[] }[];
  availableWells: string[];
};

// Fields that are safe to persist to sessionStorage for the 3D Viewer.
// Excludes: onOptionChange (function), modelSources/ipdbSources/availableWells
// (derived from API/store at runtime), and dimension fields (derived from Redux).
export type ThreeDViewerPersistedStateT = Pick<
  Omit<ThreeDViewerOptionT, "onOptionChange">,
  | "scaleZ"
  | "showDrainage"
  | "showIPDBDrainage"
  | "showVerticalPortion"
  | "lineWidth"
  | "showIPDBPlane"
  | "showIPDBMesh"
  | "highlightHzLeg"
  | "showMultiLegWells"
  | "hzLegColor"
  | "wireframe"
  | "showWellLabel"
  | "showWellDetail"
  | "colorMap"
  | "colorMapMin"
  | "colorMapMax"
  | "ipdbField"
  | "ipdbFieldUnit"
  | "maxValue"
  | "modelSource"
  | "ipdbSource"
  | "scaleDrainage"
  | "showVertical"
>;

/**
 * Where a 3D viewer with this `id` keeps the options above for the session.
 *
 * It lives here beside the shape it stores, rather than in the viewer, so that
 * both the viewer that writes the slot and any host that reads it back name it
 * the same way. Spelled out twice the two would drift apart in silence, and all
 * the user would see is settings that do not come back.
 */
export const threeDSessionKey = (id: string): string => `threed::${id}`;

// XDA Viewer

/**
 * Where an XDA viewer with this `id` keeps its state for the session.
 *
 * Here for the same reason `threeDSessionKey` is: the viewer writes the slot
 * and the well card panel reads it back, and spelled out at both ends the two
 * drift apart in silence — all the user sees is settings that do not come back.
 * `widgetType` is what a dashboard widget files itself under; anything without
 * one is a plain cross-section.
 */
export const xdaSessionKey = (id: string, widgetType?: string): string =>
  `${widgetType ?? "xda"}::${id}`;

export type Point = {
  x: number;
  y: number;
};
export type Line = {
  start: Point;
  end: Point;
};
export type MutationParameters = {
  line: Line;
  wells: string[];
  dataFields: IGroupByMap[];
  groupMode?: boolean;
};

export type XdaConstraintT = {
  XDAWidth: number;
  XDAHeight: number;
  constrainXDABy: string;
  xdaHeightUp?: number;
  xdaHeightDown?: number;
  xdaWidthLeft?: number;
  xdaWidthRight?: number;
};
